import React, { useState } from 'react';

import Box from '@amzn/meridian/box';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import { type ErrorRecord, type HandleChange, type UpdatePartial } from 'src/hooks/useForm';
import { MeasureUnitValues } from 'src/services/tasks/constants';
import { type TaskTableDataRow } from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { useAppSelector } from 'src/store/store';
import { createSearchRegExp } from 'src/utils/searchUtils';

import WeightRestrictionSelector from './weightRestrictionSelector';

// **** Types **** //

interface TaskModalFormProps {
  formData: TaskTableDataRow;
  errors: ErrorRecord<TaskTableDataRow>;
  handleChange: HandleChange<TaskTableDataRow>;
  updatePartial: UpdatePartial<TaskTableDataRow>;
}

// **** Components **** //

/**
 * Default Component
 */
const TaskModalForm = (props: TaskModalFormProps) => {
  const { formData, errors, handleChange, updatePartial } = props;
  const { regions, unitsOfMeasure } = useAppSelector((state) => state.taskConfigPage);
  const [searchQuery, setSearchQuery] = useState();
  const displayWeightRangesSelector = formData.isMeasuredBy === MeasureUnitValues.Hours;

  const searchRegExp = createSearchRegExp(searchQuery);
  const matchedRegionOptions = regions
    .map((region) => ({ label: region.name, value: region.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));

  return (
    <Column>
      {/* Task name  */}
      <Row widths="fill" spacingInset="none none 200 none">
        <Input
          label="Task Name"
          type="text"
          placeholder="Enter task name..."
          value={formData.label}
          onChange={(v) => handleChange({ label: v })}
          error={errors.label?.error}
          errorMessage={errors.label?.errorMessage}
        />
      </Row>

      {/* Region */}
      <Row widths="fill" spacingInset="none none 200 none">
        <Select
          label="Region/State/Province"
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select region..."
          value={formData.regions}
          onChange={(v) => handleChange({ regions: v })}
          error={errors.regions?.error}
          errorMessage={errors.regions?.errorMessage}
        >
          {matchedRegionOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>

      {/* Description */}
      <Row widths="fill" spacingInset="none none 200 none">
        <Input
          label="Description"
          placeholder="Enter description..."
          value={formData.description}
          onChange={(v) => handleChange({ description: v })}
          error={errors.description?.error}
          errorMessage={errors.description?.errorMessage}
          type="text"
        />
      </Row>

      {/* Identify Body Location */}
      <Row spacingInset="200 none 300 none">
        <Row alignmentHorizontal="start" spacing="300">
          <Box>
            <Text>Identify Body Location (L,R,B)?</Text>
          </Box>
          <Box>
            <Checkbox
              checked={formData.identifyBodyLocation === true}
              onChange={(v) => handleChange({ identifyBodyLocation: v }, (v) => !!v)}
            />
          </Box>
        </Row>
      </Row>

      {/* Unit of Measure */}
      <Row widths="fill" spacingInset={`none none ${displayWeightRangesSelector ? '200' : '300'} none`}>
        <Select
          label="Unit of Measure"
          placeholder="Select unit of measure..."
          value={formData.isMeasuredBy}
          onChange={(isMeasuredBy) => {
            updatePartial({ isMeasuredBy, restrictionMaxValue: null, restrictionMaxUnitName: null });
          }}
          error={errors.isMeasuredBy?.error}
          errorMessage={errors.isMeasuredBy?.errorMessage}
        >
          {unitsOfMeasure.map((option) => (
            <SelectOption label={option.name} value={option.name} key={option.id} />
          ))}
        </Select>
      </Row>

      {/* Select Weight range if Hours */}
      {displayWeightRangesSelector ? (
        <Box spacingInset="400 none 300 none">
          <Divider />
          <WeightRestrictionSelector formData={formData} handleChange={handleChange} errors={errors} />
        </Box>
      ) : (
        <Box spacingInset="200 none 200 none" />
      )}
    </Column>
  );
};

// **** Export **** //

export default TaskModalForm;
